import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import RichTextPreview from '../richTextPreview';
import NotificationCard from '../notificationCard';
import { isArrayEmpty } from '../../../utility/helpers';
import { formatDate } from '../../../utility/dateUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    backgroundColor: primary.white,
    borderRadius: 16,
    border: `1px solid ${primary.bluish7}`,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(6),
    height: 271,
    transition: 'border-color 0.2s ease',
    '&:hover': {
      borderColor: primary.blue1,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: spacing(4),
  },
  iconWrapper: props => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    width: 40,
    height: 40,
    marginRight: spacing(3),
    flexShrink: 0,
    backgroundColor: props.backgroundColor,
    '& path': {
      fill: props.color,
    },
  }),
  icon: {
    width: 20,
    height: 20,
  },
  titleWrapper: {
    flex: 1,
  },
  count: {
    color: primary.bluish4,
    lineHeight: '16px',
  },
  description: {
    color: primary.bluish4,
    marginBottom: spacing(5),
  },
  announcements: {
    display: 'flex',
    flexDirection: 'column',
  },
  announcement: {
    display: 'flex',
    alignItems: 'flex-start',
    minHeight: 40,
    overflow: 'hidden',
    paddingBottom: spacing(3),
    '&:not(:first-of-type)': {
      borderTop: `1px solid ${primary.bluish7}`,
      paddingTop: spacing(3),
    },
    '&:last-of-type': {
      paddingBottom: 0,
    },
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: spacing(3),
    marginTop: spacing(1.5),
    flexShrink: 0,
  },
  announcementContent: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    marginBottom: spacing(1),
    lineHeight: '18px',
  },
  preview: {
    color: primary.bluish3,
  },
  publishedAt: {
    color: primary.bluish4,
    marginLeft: spacing(4),
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  noAnnouncements: {
    marginTop: 'auto',
    padding: spacing(4, 3),
  },
}));

const AnnouncementCategoryCard = ({
  translations,
  category,
  announcements = [],
  totalCount = 0,
  onGoToCategoryDetails = () => {},
}) => {
  const { title, countLabel, description, noAnnouncements } = translations;
  const { color, backgroundColor, icon: Icon } = category;
  const countLabelText = countLabel.replace('[COUNT]', totalCount);
  const classes = useStyles({ color, backgroundColor });
  const DATE_FORMAT = 'MMM d';

  return (
    <div className={classes.root} onClick={onGoToCategoryDetails}>
      <div className={classes.header}>
        <div className={classes.iconWrapper}>
          <Icon className={classes.icon} />
        </div>
        <div className={classes.titleWrapper}>
          <Typography variant="h4">{title}</Typography>
          <Typography className={classes.count} variant="body2">
            {countLabelText}
          </Typography>
        </div>
      </div>
      <Typography className={classes.description} variant="subtitle1">
        {description}
      </Typography>
      {!isArrayEmpty(announcements) && (
        <div className={classes.announcements}>
          {announcements.map(announcement => {
            return (
              <div key={announcement.id} className={classes.announcement}>
                <div
                  className={classes.dot}
                  style={{ backgroundColor: color }}
                />
                <div className={classes.announcementContent}>
                  <TextBoxWithTooltip
                    className={classes.title}
                    text={announcement.title}
                    variant="subtitle1"
                    isClickable
                  />
                  <RichTextPreview
                    className={classes.preview}
                    text={announcement.body_preview}
                    variant="body2"
                    isEllipsis
                  />
                </div>
                <Typography className={classes.publishedAt} variant="subtitle2">
                  {formatDate(announcement.published_at, DATE_FORMAT)}
                </Typography>
              </div>
            );
          })}
        </div>
      )}
      <NotificationCard
        className={classes.noAnnouncements}
        title={noAnnouncements.title}
        content={noAnnouncements.content}
        titleVariant="subtitle1"
        shouldFade={isArrayEmpty(announcements)}
        shouldFadeOutInstant
      />
    </div>
  );
};

AnnouncementCategoryCard.propTypes = {
  translations: PropTypes.object.isRequired,
  category: PropTypes.shape({
    color: PropTypes.string.isRequired,
    backgroundColor: PropTypes.string.isRequired,
    icon: PropTypes.object.isRequired,
  }).isRequired,
  announcements: PropTypes.arrayOf(PropTypes.object.isRequired),
  totalCount: PropTypes.number,
  onGoToCategoryDetails: PropTypes.func,
};

export default AnnouncementCategoryCard;
